import React, { useEffect, useState, useReducer, useCallback } from "react";

import {
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    makeStyles,
    InputAdornment,
    TextField,
    CircularProgress
} from "@material-ui/core";

import {
    DeleteOutline,
    Edit,
    Search
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import ProfessionalModal from "../../components/ProfessionalModal";

const useStyles = makeStyles(theme => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    customTableCell: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
}));

const reducer = (state, action) => {
    if (action.type === "LOAD_PROFESSIONALS") {
        const professionals = action.payload;
        const newProfessionals = [];

        professionals.forEach(professional => {
            const professionalIndex = state.findIndex(p => p.id === professional.id);
            if (professionalIndex !== -1) {
                state[professionalIndex] = professional;
            } else {
                newProfessionals.push(professional);
            }
        });

        return [...state, ...newProfessionals];
    }

    if (action.type === "UPDATE_PROFESSIONALS") {
        const professional = action.payload;
        const professionalIndex = state.findIndex(p => p.id === professional.id);

        if (professionalIndex !== -1) {
            state[professionalIndex] = professional;
            return [...state];
        } else {
            return [professional, ...state];
        }
    }

    if (action.type === "DELETE_PROFESSIONAL") {
        const professionalId = action.payload;
        const professionalIndex = state.findIndex(p => p.id === professionalId);
        if (professionalIndex !== -1) {
            state.splice(professionalIndex, 1);
        }
        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const Professionals = () => {
    const classes = useStyles();

    const [professionals, dispatch] = useReducer(reducer, []);
    const [loading, setLoading] = useState(false);
    const [professionalModalOpen, setProfessionalModalOpen] = useState(false);
    const [selectedProfessional, setSelectedProfessional] = useState(null);
    const [searchParam, setSearchParam] = useState("");
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deletingProfessional, setDeletingProfessional] = useState(null);

    useEffect(() => {
        dispatch({ type: "RESET" });
    }, [searchParam]);

    const fetchProfessionals = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/professionals", {
                params: { searchParam }
            });
            dispatch({ type: "LOAD_PROFESSIONALS", payload: data });
            setLoading(false);
        } catch (err) {
            toastError(err);
            setLoading(false);
        }
    }, [searchParam]);

    useEffect(() => {
        fetchProfessionals();
    }, [searchParam, fetchProfessionals]);

    const handleOpenProfessionalModal = () => {
        setProfessionalModalOpen(true);
        setSelectedProfessional(null);
    };

    const handleCloseProfessionalModal = () => {
        setProfessionalModalOpen(false);
        setSelectedProfessional(null);
    };

    const handleEditProfessional = (professional) => {
        setSelectedProfessional(professional);
        setProfessionalModalOpen(true);
    };

    const handleDeleteProfessional = async (professionalId) => {
        try {
            await api.delete(`/professionals/${professionalId}`);
            toast.success(i18n.t("professionals.toasts.deleted"));
            dispatch({ type: "DELETE_PROFESSIONAL", payload: professionalId }); // Correctly dispatch delete
        } catch (err) {
            toastError(err);
        }
        setDeletingProfessional(null);
    };

    return (
        <MainContainer>
            <ProfessionalModal
                open={professionalModalOpen}
                onClose={handleCloseProfessionalModal}
                professionalId={selectedProfessional && selectedProfessional.id}
                reload={fetchProfessionals}
            />
            <ConfirmationModal
                title={deletingProfessional && `${i18n.t("professionals.confirmationModal.deleteTitle")} ${deletingProfessional.name}?`}
                open={confirmModalOpen}
                onClose={setConfirmModalOpen}
                onConfirm={() => handleDeleteProfessional(deletingProfessional.id)}
            >
                {i18n.t("professionals.confirmationModal.deleteMessage")}
            </ConfirmationModal>
            <MainHeader>
                <Title>{i18n.t("professionals.title")}</Title>
                <MainHeaderButtonsWrapper>
                    <TextField
                        placeholder={i18n.t("contacts.searchPlaceholder")}
                        type="search"
                        value={searchParam}
                        onChange={(e) => setSearchParam(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="secondary" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleOpenProfessionalModal}
                    >
                        {i18n.t("professionals.buttons.add")}
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">{i18n.t("professionals.table.name")}</TableCell>
                                <TableCell align="center">{i18n.t("professionals.table.email")}</TableCell>
                                <TableCell align="center">{i18n.t("professionals.table.actions")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <>
                                {professionals.map((professional) => (
                                    <TableRow key={professional.id}>
                                        <TableCell align="center">{professional.name}</TableCell>
                                        <TableCell align="center">{professional.email}</TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditProfessional(professional)}
                                            >
                                                <Edit />
                                            </IconButton>

                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setDeletingProfessional(professional);
                                                    setConfirmModalOpen(true);
                                                }}
                                            >
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {loading && <TableRow><TableCell colSpan={3} align="center"><CircularProgress /></TableCell></TableRow>}
                            </>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </MainContainer>
    );
};

export default Professionals;
